import React, { useState } from 'react';
import { AlertTriangle, ShieldAlert, CheckCircle, Clock, MoreVertical } from 'lucide-react';
import { mockFraudAlerts } from '../../data/mockData';
export const FraudDetection = () => {
  const [severityFilter, setSeverityFilter] = useState<'all' | 'Critical' | 'High'>('all');
  const filteredAlerts = mockFraudAlerts.filter(a => severityFilter === 'all' ? true : a.severity === severityFilter);
  return <div className="space-y-8 animate-in fade-in" data-id="element-650">
      <div data-id="element-651">
        <h1 className="text-2xl font-serif font-bold text-slate-900 dark:text-white mb-1" data-id="element-652">
          Fraud Detection
        </h1>
        <p className="text-slate-500 dark:text-slate-400" data-id="element-653">
          AI-flagged anomalies and suspicious activities.
        </p>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-id="element-654">
        {[{
        label: 'Active Alerts',
        value: '3',
        icon: AlertTriangle,
        color: 'text-red-500 dark:text-red-400',
        bg: 'bg-red-50 dark:bg-red-400/10'
      }, {
        label: 'Resolved Today',
        value: '12',
        icon: CheckCircle,
        color: 'text-emerald-500 dark:text-emerald-400',
        bg: 'bg-emerald-50 dark:bg-emerald-400/10'
      }, {
        label: 'Avg Resolution',
        value: '2.4h',
        icon: Clock,
        color: 'text-blue-500 dark:text-blue-400',
        bg: 'bg-blue-50 dark:bg-blue-400/10'
      }, {
        label: 'High Severity',
        value: '1',
        icon: ShieldAlert,
        color: 'text-orange-500 dark:text-orange-400',
        bg: 'bg-orange-50 dark:bg-orange-400/10'
      }].map((stat, i) => {
        const Icon = stat.icon;
        return <div key={i} className="bg-white dark:bg-[#1e293b] p-5 rounded-2xl border border-slate-200 dark:border-slate-700 flex items-center gap-4 transition-colors duration-200" data-id="element-655">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${stat.bg} ${stat.color}`} data-id="element-656">
                <Icon size={24} data-id="element-657" />
              </div>
              <div data-id="element-658">
                <div className="text-2xl font-bold text-slate-900 dark:text-white" data-id="element-659">
                  {stat.value}
                </div>
                <div className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider" data-id="element-660">
                  {stat.label}
                </div>
              </div>
            </div>;
      })}
      </div>

      {/* Table */}
      <div className="bg-white dark:bg-[#1e293b] rounded-2xl border border-slate-200 dark:border-slate-700 overflow-hidden transition-colors duration-200" data-id="element-661">
        <div className="p-4 border-b border-slate-200 dark:border-slate-700 flex justify-between items-center bg-slate-50 dark:bg-slate-800/50" data-id="element-662">
          <div className="flex gap-2" data-id="element-663">
            {([{
            key: 'all',
            label: 'All Alerts'
          }, {
            key: 'Critical',
            label: 'Critical'
          }, {
            key: 'High',
            label: 'High'
          }] as const).map(tab => <button key={tab.key} onClick={() => setSeverityFilter(tab.key)} className={`px-4 py-1.5 rounded-lg text-sm transition-colors ${severityFilter === tab.key ? 'bg-slate-200 dark:bg-slate-700 text-slate-900 dark:text-white font-bold' : 'text-slate-500 dark:text-slate-400 font-medium hover:bg-slate-100 dark:hover:bg-slate-800'}`} data-id="element-664">
                {tab.label}
              </button>)}
          </div>
        </div>

        <div className="overflow-x-auto" data-id="element-665">
          <table className="w-full text-left border-collapse" data-id="element-666">
            <thead data-id="element-667">
              <tr className="bg-slate-50 dark:bg-slate-900/50 text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider border-b border-slate-200 dark:border-slate-700" data-id="element-668">
                <th className="p-4 pl-6" data-id="element-669">Alert Type</th>
                <th className="p-4" data-id="element-670">Property</th>
                <th className="p-4" data-id="element-671">Seller</th>
                <th className="p-4" data-id="element-672">Severity</th>
                <th className="p-4" data-id="element-673">Status</th>
                <th className="p-4" data-id="element-674">Date</th>
                <th className="p-4 pr-6 text-right" data-id="element-675">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 dark:divide-slate-700/50" data-id="element-676">
              {filteredAlerts.length === 0 ? <tr data-id="element-677">
                  <td colSpan={7} className="p-8 text-center text-sm text-slate-500 dark:text-slate-400" data-id="element-678">
                    No alerts match this filter.
                  </td>
                </tr> : filteredAlerts.map(alert => <tr key={alert.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors" data-id="element-679">
                    <td className="p-4 pl-6 font-bold text-slate-900 dark:text-slate-200" data-id="element-680">
                      {alert.type}
                    </td>
                    <td className="p-4 text-sm text-slate-700 dark:text-slate-300" data-id="element-681">
                      {alert.property}
                    </td>
                    <td className="p-4 text-sm text-slate-700 dark:text-slate-300" data-id="element-682">
                      {alert.seller}
                    </td>
                    <td className="p-4" data-id="element-683">
                      <span className={`px-2 py-1 text-[10px] font-bold uppercase rounded ${alert.severity === 'Critical' ? 'bg-red-50 dark:bg-red-500/10 text-red-600 dark:text-red-400 border border-red-200 dark:border-red-500/20' : alert.severity === 'High' ? 'bg-orange-50 dark:bg-orange-500/10 text-orange-600 dark:text-orange-400 border border-orange-200 dark:border-orange-500/20' : 'bg-yellow-50 dark:bg-yellow-500/10 text-yellow-600 dark:text-yellow-400 border border-yellow-200 dark:border-yellow-500/20'}`} data-id="element-684">
                        {alert.severity}
                      </span>
                    </td>
                    <td className="p-4" data-id="element-685">
                      <span className={`px-2 py-1 text-[10px] font-bold uppercase rounded ${alert.status === 'Resolved' ? 'bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400' : 'bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300'}`} data-id="element-686">
                        {alert.status}
                      </span>
                    </td>
                    <td className="p-4 text-sm text-slate-500 dark:text-slate-400" data-id="element-687">
                      {alert.date}
                    </td>
                    <td className="p-4 pr-6 text-right" data-id="element-688">
                      <button className="p-2 text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors" data-id="element-689">
                        <MoreVertical size={18} data-id="element-690" />
                      </button>
                    </td>
                  </tr>)}
            </tbody>
          </table>
        </div>
      </div>
    </div>;
};